import { ArrowRight, Check, ChevronDown, ListChecks, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import {
  SETUP_STEPS,
  clearWelcomePending,
  dismissChurchSetup,
  loadChurchSetup,
  markSetupStepComplete,
  setSetupCollapsed,
  type ChurchSetupState,
} from '@/lib/church-setup';
import { cn } from '@/utils/cn';

import { WelcomeDialog } from './welcome-dialog';

export interface SetupChecklistProps {
  churchId: string;
  churchName: string;
}

/**
 * First-church setup guide: welcome dialog + floating checklist.
 * Auto-completes steps when the user visits the matching routes.
 */
export const SetupChecklist = ({ churchId, churchName }: SetupChecklistProps) => {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const location = useLocation();

  const [state, setState] = useState<ChurchSetupState>(() => loadChurchSetup(churchId));
  const completedRef = useRef(new Set(state.completedSteps));
  completedRef.current = new Set(state.completedSteps);

  useEffect(() => {
    const next = loadChurchSetup(churchId);
    setState(next);
    completedRef.current = new Set(next.completedSteps);
  }, [churchId]);

  useEffect(() => {
    if (state.dismissed) return;
    const { pathname } = location;
    const prev = completedRef.current;
    let changed = false;
    let next = prev;

    for (const step of SETUP_STEPS) {
      if (pathname.startsWith(step.completesOnPath) && !prev.has(step.id)) {
        if (!changed) {
          next = new Set(prev);
          changed = true;
        }
        next.add(step.id);
        markSetupStepComplete(churchId, step.id);
      }
    }

    if (changed) {
      const updated = loadChurchSetup(churchId);
      setState(updated);
      completedRef.current = new Set(updated.completedSteps);

      if (updated.completedSteps.length >= SETUP_STEPS.length) {
        const timer = window.setTimeout(() => {
          dismissChurchSetup(churchId);
          setState(loadChurchSetup(churchId));
        }, 2500);
        return () => window.clearTimeout(timer);
      }
    }
  }, [location.pathname, churchId, state.dismissed]);

  if (state.dismissed) return null;

  const completed = new Set(state.completedSteps);
  const doneCount = SETUP_STEPS.filter((step) => completed.has(step.id)).length;
  const allDone = doneCount === SETUP_STEPS.length;
  const currentStep = SETUP_STEPS.find((step) => !completed.has(step.id));
  const progress = Math.round((doneCount / SETUP_STEPS.length) * 100);

  const handleStartSetup = () => {
    clearWelcomePending(churchId, { collapsed: false });
    setState(loadChurchSetup(churchId));
  };

  const handleExplore = () => {
    clearWelcomePending(churchId, { collapsed: true });
    setState(loadChurchSetup(churchId));
  };

  const handleDismiss = () => {
    dismissChurchSetup(churchId);
    setState(loadChurchSetup(churchId));
  };

  const handleCollapse = (collapsed: boolean) => {
    setSetupCollapsed(churchId, collapsed);
    setState(loadChurchSetup(churchId));
  };

  const handleAction = (actionPath: string) => {
    void navigate(actionPath);
  };

  return (
    <>
      <WelcomeDialog
        open={state.welcomePending}
        churchName={churchName}
        onStartSetup={handleStartSetup}
        onExplore={handleExplore}
        onBringRecords={() => {
          clearWelcomePending(churchId, { collapsed: true });
          setState(loadChurchSetup(churchId));
          void navigate('/settings/migration-assistant?onboarding=1');
        }}
      />

      {!state.welcomePending ? (
        state.collapsed ? (
          <div className="fixed bottom-6 right-6 z-40">
            <Button
              type="button"
              variant="outline"
              className="gap-2 rounded-full shadow-e3"
              onClick={() => handleCollapse(false)}
              aria-label={t('checklist.expandAria')}
            >
              <ListChecks className="size-3.5 text-brand" aria-hidden />
              {t('checklist.title')}
              <span className="text-caption text-fg-tertiary">
                {t('checklist.progress', { done: doneCount, total: SETUP_STEPS.length })}
              </span>
            </Button>
          </div>
        ) : (
          <div className="fixed bottom-2 left-2 right-2 z-40 overflow-hidden rounded-xl border border-border bg-surface shadow-e3 sm:bottom-6 sm:left-auto sm:right-6 sm:w-[20rem]">
            <div className="flex items-center gap-2 px-4 pb-2 pt-3">
              <ListChecks className="size-4 shrink-0 text-brand" aria-hidden />
              <span className="flex-1 text-body-sm font-semibold text-fg">
                {allDone ? t('checklist.complete') : t('checklist.title')}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 text-fg-tertiary hover:text-fg"
                onClick={() => handleCollapse(true)}
                aria-label={t('checklist.collapseAria')}
              >
                <ChevronDown className="size-4" aria-hidden />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 text-fg-tertiary hover:text-fg"
                onClick={handleDismiss}
                aria-label={t('checklist.dismissAria')}
              >
                <X className="size-4" aria-hidden />
              </Button>
            </div>

            <div className="px-4 pb-3">
              <div className="mb-1.5 flex justify-between text-caption text-fg-tertiary">
                <span>{t('checklist.progress', { done: doneCount, total: SETUP_STEPS.length })}</span>
                <span>{progress}%</span>
              </div>
              <div className="h-1 w-full overflow-hidden rounded-full bg-surface-sunken">
                <div
                  className="h-full rounded-full bg-brand transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            <div className="border-t border-border">
              {allDone ? (
                <p className="px-4 py-3 text-caption text-fg-secondary">{t('checklist.doneBody')}</p>
              ) : (
                SETUP_STEPS.map((step) => {
                  const isDone = completed.has(step.id);
                  const isCurrent = currentStep?.id === step.id;

                  return (
                    <div
                      key={step.id}
                      className={cn(
                        'border-b border-border px-4 py-3 last:border-b-0',
                        isCurrent && 'bg-brand/5',
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={cn(
                            'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border-2',
                            isDone && 'border-brand bg-brand text-fg-inverse',
                            isCurrent && !isDone && 'border-brand bg-surface',
                            !isDone && !isCurrent && 'border-border bg-surface',
                          )}
                        >
                          {isDone ? <Check className="size-3" aria-hidden /> : null}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p
                            className={cn(
                              'text-body-sm font-medium leading-tight',
                              isDone && 'text-fg-tertiary line-through',
                              isCurrent && 'text-fg',
                              !isDone && !isCurrent && 'text-fg-secondary',
                            )}
                          >
                            {t(step.titleKey)}
                          </p>
                          {isCurrent ? (
                            <>
                              <p className="mt-1 text-caption leading-relaxed text-fg-secondary">
                                {t(step.descKey)}
                              </p>
                              <Button
                                type="button"
                                size="sm"
                                className="mt-2.5 gap-1.5"
                                onClick={() => handleAction(step.actionPath)}
                              >
                                {t(step.actionKey)}
                                <ArrowRight className="size-3" aria-hidden />
                              </Button>
                            </>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {allDone ? (
              <div className="flex justify-end px-4 py-3">
                <Button type="button" size="sm" onClick={handleDismiss}>
                  {t('checklist.dismiss')}
                </Button>
              </div>
            ) : null}
          </div>
        )
      ) : null}
    </>
  );
};
